import React, { useState, useEffect, useRef } from 'react';
import { LogIn, UserPlus, Coins, ChevronDown, LogOut, Receipt, Loader2 } from 'lucide-react';
import type { UserProfile } from '../types';
import { openBillingPortal } from '../services/supabase';

interface HeaderProps {
  user: UserProfile | null;
  onLogin: () => void;
  onLogout: () => void;
  onSignUp?: () => void;
  onOpenCredits?: () => void;
}

const Header: React.FC<HeaderProps> = ({ user, onLogin, onLogout, onSignUp, onOpenCredits }) => {
  const [accountOpen, setAccountOpen] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingNotice, setBillingNotice] = useState<string | null>(null);
  const accountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) setAccountOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const handleBillingPortal = async () => {
    if (billingLoading) return;
    setBillingNotice(null);
    setBillingLoading(true);
    try {
      const result = await openBillingPortal();
      if ('noCustomer' in result) {
        setBillingNotice("No invoices yet — buy credits or a plan and they'll appear here.");
      } else {
        window.location.href = result.url;
      }
    } catch (err) {
      console.error('[Billing] portal failed:', err);
      setBillingNotice("Couldn't open billing right now. Please try again.");
    } finally {
      setBillingLoading(false);
    }
  };

  return (
    <header
      className="h-14 flex items-center justify-between px-4 shrink-0 border-b"
      style={{ backgroundColor: '#1c1c28', borderColor: '#2e2e40' }}
    >
      <div className="flex items-center gap-2">
        <a href="/" className="flex items-center shrink-0 h-8 w-[120px] sm:w-[150px]">
          <img src="/Floor Plan Logo.png" alt="Floor Plan" className="h-8 sm:h-10 w-auto max-w-full object-contain object-left" />
        </a>
      </div>
      <div className="flex items-center gap-3 shrink-0 ml-auto" ref={accountRef}>
        {user ? (
          <>
            <button
              onClick={onOpenCredits}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium"
              style={{ backgroundColor: '#FF4C4C', color: '#fff' }}
            >
              <Coins size={16} />
              {user.credits} credits
            </button>
            <div className="relative">
              <button
                onClick={() => setAccountOpen((o) => !o)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
                style={{ color: '#f0f0f5', backgroundColor: 'transparent' }}
              >
                <span className="max-w-[120px] truncate text-sm">{user.email || 'Account'}</span>
                <ChevronDown size={16} className={`transition-transform ${accountOpen ? 'rotate-180' : ''}`} />
              </button>
              {accountOpen && (
                <div
                  className="absolute right-0 top-full mt-1 py-1 min-w-[180px] rounded-xl shadow-xl z-50"
                  style={{ backgroundColor: '#252532', border: '1px solid #2e2e40' }}
                >
                  <button
                    onClick={handleBillingPortal}
                    disabled={billingLoading}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm rounded-lg disabled:opacity-60"
                    style={{ color: '#f0f0f5' }}
                  >
                    {billingLoading ? <Loader2 size={16} className="animate-spin" /> : <Receipt size={16} />}
                    Billing &amp; invoices
                  </button>
                  {billingNotice && (
                    <div className="px-4 py-2 text-xs text-amber-300/90" style={{ borderTop: '1px solid #2e2e40' }}>{billingNotice}</div>
                  )}
                  <button
                    onClick={() => {
                      onLogout();
                      setAccountOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm rounded-lg"
                    style={{ color: '#f0f0f5' }}
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={onLogin}
              className="px-3 py-2 rounded-lg text-sm font-medium"
              style={{ color: '#8888aa' }}
            >
              <LogIn size={18} className="inline mr-1" />
              Log In
            </button>
            <button
              onClick={onSignUp ?? onLogin}
              className="px-4 py-2 rounded-lg text-sm font-semibold"
              style={{ backgroundColor: '#FF4C4C', color: '#fff' }}
            >
              <UserPlus size={18} className="inline mr-1" />
              Sign Up
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
